import PropTypes from 'prop-types'
import { Typography } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import { createUseStyles } from 'react-jss'
import capitalize from 'lodash/capitalize'
import { standardDateTime } from '../../lib/datetime'
import PatientEntry from '../Common/PatientEntry'

const { Text } = Typography

// Width of antd's expand-icon column. The expanded details are padded by the
// same amount so their columns line up with the primary card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  // Same grid, indented to align with the primary card past the expand column.
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  // Lets a long field take the full width of the 2-column details grid.
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

// "sms" reads badly capitalized, every other channel is a regular word.
export function messageTypeLabel(messageType) {
  if (!messageType) return ''
  if (messageType === 'sms') return messageType.toUpperCase()
  return capitalize(messageType)
}

// Antd docs mention type="success" but it does not work, it might be a bug in
// the version we're using. Use "copy-success" instead.
function statusTextType(status) {
  switch (status) {
    case 'completed':
    case 'delivered':
      return 'copy-success'
    case 'queued':
      return 'warning'
    default:
      return 'info'
  }
}

// Shared by the desktop "Status" column and the mobile card.
export function ReminderStatusText({ status = undefined }) {
  return <Text type={statusTextType(status)}>{status}</Text>
}

ReminderStatusText.propTypes = {
  status: PropTypes.string,
}

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Always-visible summary shown for every reminder row on mobile.
export function ReminderMobileCard({ reminder }) {
  const classes = useStyles()
  const { createdAt, messageType, patient, status } = reminder
  const typeLabel = messageTypeLabel(messageType)

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {(status || typeLabel) && (
        <div className={classes.line}>
          {status && <ReminderStatusText status={status} />}
          {status && typeLabel ? ' · ' : null}
          {typeLabel}
        </div>
      )}
      <div className={classes.line}>
        <CalendarOutlined className={classes.lineIcon} />
        {/* standardDateTime(undefined) would render the current time. */}
        {createdAt ? standardDateTime(createdAt) : '—'}
      </div>
    </div>
  )
}

ReminderMobileCard.propTypes = {
  reminder: PropTypes.shape({
    createdAt: PropTypes.string,
    messageType: PropTypes.string,
    status: PropTypes.string,
    patient: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    }),
  }).isRequired,
}

// Fields hidden behind the per-row expand toggle.
export function ReminderMobileDetails({ reminder }) {
  const classes = useStyles()
  const { errorCode, errorMessage, patient, to, updatedAt } = reminder

  return (
    <div className={classes.detailsGrid}>
      <Field label="To">{to || '—'}</Field>
      <Field label="Error code">
        {errorCode ? <Text type="danger">{errorCode}</Text> : '—'}
      </Field>
      <Field label="Updated on" span>
        {updatedAt ? standardDateTime(updatedAt) : '—'}
      </Field>
      <Field label="Practice" span>
        {patient?.practice?.name || '—'}
      </Field>
      <Field label="Error message" span>
        {errorMessage ? <Text type="danger">{errorMessage}</Text> : '—'}
      </Field>
    </div>
  )
}

ReminderMobileDetails.propTypes = {
  reminder: PropTypes.shape({
    to: PropTypes.string,
    updatedAt: PropTypes.string,
    errorCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    errorMessage: PropTypes.string,
    patient: PropTypes.shape({
      practice: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
}
